import type { MarketListing } from "../types/market";

// Local-only sample data for the Market page's Live view. The local database
// lags prod, so Live is empty or stale when developing; this adds a few real
// listings copied from prod (2026-11-04) with end times relative to "now", so
// the Time left countdown and the Ending soon label always have something to
// show. Only used when the API is localhost - see Market.tsx.

const MIN = 60 * 1000;
const HOUR = 60 * MIN;

const color = (hex: string, text: string) =>
  `<span style="color:${hex}">${text}</span>`;
const polished = (rest: string) =>
  `${color("#00ff00", "(")}${color("#ffffff", "Polished")}${color("#00ff00", ")")}${color("#c0c0c0", " ")}${rest}`;

type Sample = Pick<
  MarketListing,
  "num" | "level" | "seller" | "itemNamePlain" | "itemNameHtml" | "category" | "itemType"
> &
  Partial<MarketListing> & { endsInMs: number; listedAgoMs: number };

const SAMPLES: Sample[] = [
  {
    num: 90010,
    level: 140,
    seller: "Xaxz",
    itemNamePlain: "(Polished) a blue tunic",
    itemNameHtml: polished(color("#c0c0c0", "a blue tunic")),
    category: "Bonus loot",
    itemType: "Armor",
    lastBidder: "Carnivaen",
    lastBidAmount: 1000,
    bidCount: 1,
    endsInMs: 3 * MIN,
    listedAgoMs: 47 * HOUR + 57 * MIN
  },
  {
    num: 90001,
    level: 200,
    seller: "Xaxz",
    itemNamePlain: "(Polished) Wolf-Head Mask",
    itemNameHtml: polished(
      color("#ff0000", "W") + color("#800000", "ol") + color("#ff0000", "f-Head ") + color("#808000", "Mask")
    ),
    category: "Bonus loot",
    itemType: "Armor",
    endsInMs: 12 * MIN,
    listedAgoMs: 47 * HOUR + 48 * MIN
  },
  {
    num: 90002,
    level: 174,
    seller: "Xaxz",
    itemNamePlain: "(Polished) Gwall's gnarled bracers",
    itemNameHtml: polished(color("#ff0000", "G") + color("#808080", "wall's gnarled bracers")),
    category: "Bonus loot",
    itemType: "Armor",
    lastBidder: "Carnivaen",
    lastBidAmount: 100000,
    bidCount: 2,
    endsInMs: 47 * MIN,
    listedAgoMs: 47 * HOUR + 13 * MIN
  },
  {
    num: 90003,
    level: 200,
    seller: "Xaxz",
    itemNamePlain: "(Polished) youthful violence",
    itemNameHtml: polished(color("#c0c0c0", "youthful ") + color("#5fafff", "violence")),
    category: "Bonus loot",
    itemType: "Armor",
    endsInMs: 2 * HOUR + 10 * MIN,
    listedAgoMs: 45 * HOUR + 50 * MIN
  },
  {
    num: 90004,
    level: 1,
    seller: "Gyoza",
    itemNamePlain: "(Aarchaeology) Dorothy's Lost Earring",
    itemNameHtml:
      color("#ff0000", "(") + color("#ffff00", "Aarchaeology") + color("#ff0000", ")") + color("#ffffff", " Dorothy's Lost Earring"),
    category: "Aarchaeology",
    itemType: "Treasure",
    lastBidder: "WarBurger",
    lastBidAmount: 1680367,
    bidCount: 4,
    endsInMs: 7 * HOUR + 40 * MIN,
    listedAgoMs: 63 * HOUR
  },
  {
    num: 90005,
    level: 37,
    seller: "Madrox",
    itemNamePlain: "(Radiant) Red Velvet Bed Jacket",
    itemNameHtml:
      color("#ff0000", "(") + color("#ffffff", "Radiant") + color("#ff0000", ")") + color("#c0c0c0", " ") +
      color("#ff0000", "Red ") + color("#800000", "Velvet ") + color("#ff0000", "Bed ") + color("#800000", "Jacket"),
    category: "Bonus loot",
    itemType: "Armor",
    buyoutPrice: 10000000,
    endsInMs: 21 * HOUR,
    listedAgoMs: 51 * HOUR
  },
  {
    num: 90006,
    level: 1,
    seller: "Sazed",
    itemNamePlain: "Daily Blessing Qreset Token",
    itemNameHtml: color("#ffffff", "Daily Blessing ") + color("#00ff00", "Qreset Token"),
    category: "Tokens",
    itemType: "Treasure",
    lastBidder: "Haike",
    lastBidAmount: 25600000,
    bidCount: 7,
    endsInMs: 27 * HOUR,
    listedAgoMs: 6 * 24 * HOUR
  },
  {
    num: 90007,
    level: 120,
    seller: "Madrox",
    itemNamePlain: "Hatshepsut's Cartouche",
    itemNameHtml:
      color("#0000ff", "Hats") + color("#ffffff", "heps") + color("#0000ff", "ut's C") + color("#ffffff", "arto") + color("#0000ff", "uche"),
    category: "Portals",
    itemType: "Portal",
    buyoutPrice: 5000000,
    endsInMs: 29 * HOUR,
    listedAgoMs: 43 * HOUR
  },
  {
    num: 90008,
    level: 201,
    seller: "ThumTak",
    itemNamePlain: "Thirstan Fantasy Series Collector's Card",
    itemNameHtml: color("#ff0000", "Thirstan") + color("#ffff00", " Fantasy Series Collector's Card"),
    category: "Winds",
    itemType: "Treasure",
    lastBidder: "Selitos",
    lastBidAmount: 6250500,
    bidCount: 12,
    endsInMs: 30 * HOUR,
    listedAgoMs: 42 * HOUR
  },
  {
    num: 90009,
    level: 166,
    seller: "Axl",
    itemNamePlain: "(Shiny) a diamond tipped long sword",
    itemNameHtml:
      color("#00ff00", "(") + color("#ffffff", "Shiny") + color("#00ff00", ")") + color("#c0c0c0", " ") +
      color("#000080", "a ") + color("#ffffff", "diamond") + color("#000080", " tipped long sword"),
    category: "Bonus loot",
    itemType: "Weapon",
    averageDam: 466,
    endsInMs: 51 * HOUR,
    listedAgoMs: 21 * HOUR
  }
];

export const getDevLiveListings = (now = Date.now()): MarketListing[] =>
  SAMPLES.map(({ endsInMs, listedAgoMs, ...sample }, index) => ({
    id: 900000 + index,
    currency: "Gold",
    itemNameRaw: null,
    buyoutPrice: null,
    status: "listed",
    soldTo: null,
    soldPrice: null,
    soldAt: null,
    lastBidder: null,
    lastBidAmount: null,
    bidCount: 0,
    keywords: null,
    score: null,
    averageDam: null,
    material: null,
    flags: null,
    foundAt: null,
    wearable: null,
    statMods: null,
    resistMods: null,
    enchantMods: null,
    rawCaptureHtml: null,
    ...sample,
    listedAt: new Date(now - listedAgoMs).toISOString(),
    endsAt: new Date(now + endsInMs).toISOString()
  }));
